import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from 'react-router-dom';

import { Toaster, TooltipProvider } from '@/components/cargofl-ui';
import { ServerDownOverlay } from '@/components/server-down-overlay';
import { AppShell } from '@/components/shell/app-shell';
import { FullPageSpinner } from '@/components/shell/full-page-spinner';
import { ApiError } from '@/lib/api';
import { ThemeProvider } from '@/lib/theme';
import { SESSION_QUERY_KEY, useSession, type Session } from '@/lib/session';
import { AdminPeoplePage } from '@/routes/admin/people';
import { AuditPage } from '@/routes/admin/audit';
import { ContractsPage } from '@/routes/admin/contracts';
import { AbsencePage } from '@/routes/absence';
import { ApprovalsPage } from '@/routes/approvals';
import { ReportsPage } from '@/routes/reports';
import { AdminSetupPage } from '@/routes/admin/setup';
import { SystemPage } from '@/routes/admin/system';
import { AllTimesPage } from '@/routes/all-times';
import { CalendarPage } from '@/routes/calendar';
import { ExpensesPage } from '@/routes/expenses';
import { ImportPage } from '@/routes/import';
import { InvoicesPage } from '@/routes/invoices';
import { ProfilePage } from '@/routes/profile';
import { AbsenceCalendarPage } from '@/routes/absence-calendar';
import { AbsenceReportPage } from '@/routes/absence-report';
import { ExportPage } from '@/routes/export';
import { YearReportPage } from '@/routes/year-report';
import { MySubmissionsPage } from '@/routes/my-submissions';
import { ProjectMoneyPage } from '@/routes/project-money';
import { ReportingPage } from '@/routes/reporting';
import { TeamPage } from '@/routes/team';
import { LoginPage } from '@/routes/login';
import { ForbiddenPage } from '@/routes/forbidden';
import { NotFoundPage } from '@/routes/not-found';
import { OverviewPage } from '@/routes/overview';
import { TimesheetPage } from '@/routes/timesheet';
import { atLeast, type Role } from '@cfl/shared';

/**
 * A 401 from any call means the session expired, was signed out elsewhere, or the
 * account was disabled mid-visit. Re-asking /auth/me turns that into a null session
 * and AppRoutes sends them to sign-in - instead of every page showing its own error
 * until something happens to refetch the session. The session query is skipped: it
 * answers a 401 with null rather than throwing, and if that ever changed, invalidating
 * itself from its own error would loop.
 */
function onUnauthenticated(error: Error, queryKey?: readonly unknown[]): void {
  if (!(error instanceof ApiError) || error.status !== 401) return;
  if (queryKey?.[0] === SESSION_QUERY_KEY[0] && queryKey[1] === SESSION_QUERY_KEY[1]) return;
  void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
}

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({ onError: (error, query) => onUnauthenticated(error, query.queryKey) }),
  mutationCache: new MutationCache({ onError: (error) => onUnauthenticated(error) }),
  defaultOptions: {
    queries: {
      // A 401 is answered by the session (above), not retried here; anything else
      // gets one retry, because a single blip should not empty a page.
      retry: (failures, error) => failures < 1 && !(error instanceof ApiError && error.status === 401),
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        {/* Radix tooltips throw without a provider ancestor, and nothing but running
            the page reveals it - so it wraps the whole app rather than each use. */}
        <TooltipProvider delayDuration={200}>
          <BrowserRouter>
            <AppRoutes />
            <Toaster />
          </BrowserRouter>
        </TooltipProvider>
        <ServerDownOverlay />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function AppRoutes() {
  const { data: session, isPending } = useSession();

  // Held until the session resolves. Rendering the sign-in page first would flash it
  // at every already-signed-in person on every reload.
  if (isPending) return <FullPageSpinner />;

  if (!session) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route element={<AppShell session={session} />}>
        <Route index element={<OverviewPage session={session} />} />
        <Route path="timesheet" element={<TimesheetPage session={session} />} />
        {/* The grid is a view of /timesheet now, not a page of its own. Kept as a
            redirect because this URL is in people's bookmarks and history. */}
        <Route path="timesheet/week" element={<GridRedirect />} />
        {/* Menu destinations that are still to come. They are in the sidebar marked
            "soon" rather than omitted, so the menu says what exists - but the routes
            have to answer something rather than 404. */}
        <Route path="timesheet/calendar" element={<CalendarPage />} />
        <Route path="profile" element={<ProfilePage session={session} />} />
        <Route
          path="import"
          element={
            <RequireRole role="HR" session={session}>
              <ImportPage />
            </RequireRole>
          }
        />
        {/* Export is the same filter with a download button, so it redirects to
            All times rather than being a third screen over the same rows. */}
        <Route
          path="timesheet/export"
          element={
            <RequireRole role="SUPERVISOR" session={session}>
              <ExportPage session={session} />
            </RequireRole>
          }
        />
        <Route
          path="timesheet/all"
          element={
            <RequireRole role="SUPERVISOR" session={session}>
              <AllTimesPage session={session} />
            </RequireRole>
          }
        />
        <Route path="timesheet/submissions" element={<MySubmissionsPage />} />
        <Route
          path="reporting"
          element={
            <RequireRole role="SUPERVISOR" session={session}>
              <ReportingPage session={session} />
            </RequireRole>
          }
        />
        <Route path="reporting/team" element={<Navigate to="/reporting/team/people" replace />} />
        <Route
          path="reporting/projects"
          element={
            <RequireRole role="HR" session={session}>
              <ProjectMoneyPage />
            </RequireRole>
          }
        />
        <Route
          path="reporting/year"
          element={
            <RequireRole role="SUPERVISOR" session={session}>
              <YearReportPage />
            </RequireRole>
          }
        />

        <Route
          path="reporting/team/:tab"
          element={
            <RequireRole role="SUPERVISOR" session={session}>
              <TeamPage />
            </RequireRole>
          }
        />
        <Route path="absence" element={<AbsencePage role={session.role} />} />
        <Route
          path="absence/calendar"
          element={
            <RequireRole role="SUPERVISOR" session={session}>
              <AbsenceCalendarPage />
            </RequireRole>
          }
        />
        <Route
          path="absence/report"
          element={
            <RequireRole role="SUPERVISOR" session={session}>
              <AbsenceReportPage />
            </RequireRole>
          }
        />
        <Route
          path="approvals"
          element={
            <RequireRole role="SUPERVISOR" session={session}>
              <ApprovalsPage />
            </RequireRole>
          }
        />
        <Route
          path="reports"
          element={
            <ReportsPage session={session} />
          }
        />
        <Route path="expenses" element={<ExpensesPage session={session} />} />
        <Route
          path="invoices"
          element={
            <RequireRole role="HR" session={session}>
              <InvoicesPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/contracts"
          element={
            <RequireRole role="HR" session={session}>
              <ContractsPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/people"
          element={
            <RequireRole role="HR" session={session}>
              <AdminPeoplePage session={session} />
            </RequireRole>
          }
        />
        <Route path="admin/setup" element={<Navigate to="/admin/setup/customers" replace />} />
        <Route
          path="admin/audit"
          element={
            <RequireRole role="ADMIN" session={session}>
              <AuditPage />
            </RequireRole>
          }
        />
        <Route
          path="admin/system"
          element={
            <RequireRole role="ADMIN" session={session}>
              <SystemPage />
            </RequireRole>
          }
        />

        <Route
          path="admin/setup/:tab"
          element={
            <RequireRole role="ADMIN" session={session}>
              <AdminSetupPage />
            </RequireRole>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

/**
 * Client-side role gate.
 *
 * Courtesy only — it says this person cannot use the page, rather than rendering one
 * whose every request is refused. The API enforces the same rule with its own guard,
 * and that is the one that counts.
 */
function RequireRole({
  role,
  session,
  children,
}: {
  role: Role;
  session: Session;
  children: React.ReactNode;
}) {
  if (!atLeast(session.role, role)) return <ForbiddenPage />;
  return <>{children}</>;
}

/**
 * The old weekly-grid URL.
 *
 * Carries `?week=` across, because that parameter was this page's whole reason
 * for existing in the URL and a bookmark to a particular week should still open
 * that week rather than silently landing on this one.
 */
function GridRedirect() {
  const [params] = useSearchParams();
  const week = params.get('week');
  return <Navigate to={`/timesheet${week ? `?week=${week}` : ''}`} replace />;
}
